/**
 * @fileoverview Sign-up form component using Supabase Auth with email verification.
 * @module shared/components/ui/SignUpForm
 */

import { ReactElement, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { supabase } from '@/lib/supabaseClient'
import { getPostAuthRedirect } from '@/lib/pendingInvite'
import { cn } from '@/shared/utils/cn'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from './index'

/**
 * Props for the SignUpForm component.
 */
interface SignUpFormProps extends React.ComponentProps<'div'> {
  /** Additional CSS classes */
  className?: string
}

/**
 * A sign-up form that creates a Supabase user and prompts for email verification.
 *
 * Behavior:
 * - Uses `supabase.auth.signUp` with redirect set to `${VITE_PUBLIC_URL}/`.
 * - On successful user creation (no session), shows a "Check your email" confirmation state.
 * - On error, renders a descriptive error message.
 *
 * @component
 * @example
 * ```tsx
 * <SignUpForm />
 * ```
 */
export function SignUpForm({ className, ...props }: SignUpFormProps): ReactElement {
  const navigate = useNavigate()
  const { t } = useTranslation('features-auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [confirmationSent, setConfirmationSent] = useState(false)

  // Create schema inside component to use translated messages
  const signUpSchema = z.object({
    email: z.string().email(t('signupForm.validation.invalidEmail')),
    password: z.string().min(8, t('signupForm.validation.passwordTooShort')),
  })

  /**
   * Handle form submission for account creation.
   * @param event Form submission event
   * @throws Error when validation or Supabase sign-up fails
   */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setError('')
    setLoading(true)

    // Validate inputs with Zod before sending to Supabase (Fail Fast)
    const parsed = signUpSchema.safeParse({ email, password })
    if (!parsed.success) {
      const first = parsed.error.issues[0]
      setError(first?.message || 'Invalid input')
      setLoading(false)
      return
    }

    try {
      const redirectBase = (import.meta.env.VITE_PUBLIC_URL as string) || window.location.origin
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: parsed.data.email,
        password: parsed.data.password,
        options: { emailRedirectTo: `${redirectBase}/` },
      })

      if (signUpError) throw signUpError

      // With required email confirmation, Supabase returns user without session
      if (data?.user && !data.session) {
        setConfirmationSent(true)
      } else {
        // In case envs auto-confirm (non-prod), navigate to pending invite or dashboard
        navigate(getPostAuthRedirect())
      }
    } catch (e) {
      const anyErr = e as { message?: string }
      setError(anyErr?.message || t('signupForm.errorFallback'))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async (): Promise<void> => {
    setError('')
    const redirectBase = (import.meta.env.VITE_PUBLIC_URL as string) || window.location.origin
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${redirectBase}/`,
      },
    })
    if (oauthError) {
      setError(oauthError.message)
    }
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>{t('signupForm.title')}</CardTitle>
          <CardDescription className="text-muted-foreground">
            {t('signupForm.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {confirmationSent ? (
            <div className="grid gap-4">
              <div className="rounded-lg bg-muted p-3 text-sm text-foreground">
                {t('signupForm.confirmationMessage')}
              </div>
              <Button type="button" onClick={() => navigate('/')} className="w-full">
                {t('signupForm.goToSignIn')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-6">
                {error && (
                  <div className="rounded-lg bg-destructive p-3 text-sm text-destructive-foreground">{error}</div>
                )}

                <div className="grid gap-3">
                  <Label htmlFor="email">{t('signupForm.emailLabel')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t('signupForm.emailPlaceholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    aria-describedby="email-error"
                  />
                </div>

                <div className="grid gap-3">
                  <Label htmlFor="password">{t('signupForm.passwordLabel')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      aria-describedby="password-error"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? t('signupForm.submitting') : t('signupForm.submit')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleSignUp}
                    disabled={loading}
                  >
                    <img src="/google-icon.png" alt="" className="size-4" />
                    {t('loginForm.loginWithGoogle', { ns: 'features-auth' })}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/')}
                    disabled={loading}
                  >
                    {t('signupForm.signInInstead')}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
